import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Toggle } from '@/components/ui/toggle';
import { PageShell, PageTopRow, ContentCard } from '@/components/ui/page-shell';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MagnifyingGlassIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/solid';
import type { ContactList } from '@/lib/services/contact-lists';
import { BackButton } from '@/components/ui/back-button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  Loader2,
  Pencil,
  ChevronDown,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useCampaign } from '@/lib/hooks/useCampaign';
import { SequenceStep } from '@/lib/services/campaigns';
import useSWR from 'swr';

import { SequenceTab } from './SequenceTab';
import { SettingsTab } from './SettingsTab';
import { ContactsTable, ToolsButton } from '@/components/contacts/ContactsTable';
import { AddContactsPanel } from '@/components/contacts/AddContactsPanel';
import { Plus } from 'lucide-react';
import { LiveFeedTab } from './LiveFeedTab';
import { SendsTab } from './SendsTab';

interface User {
  id: number;
  email: string;
  name?: string;
  role: string;
}

type ValidationError = {
  stepId: string;
  stepIndex: number;
  field: 'subject' | 'content' | 'senderEmail';
  message: string;
};

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((res) => res.json());

type TabValue = 'live-feed' | 'leads' | 'sequence' | 'sends' | 'settings';

const VALID_TABS: TabValue[] = ['live-feed', 'leads', 'sequence', 'sends', 'settings'];

function isValidTab(tab: string | null): tab is TabValue {
  return tab !== null && VALID_TABS.includes(tab as TabValue);
}

export default function CampaignEditor() {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignId = params.id as string;

  // Get tab from URL, default to 'live-feed'. The Leads tab was ?tab=contacts
  // before the rename; old links still open it and the address is corrected.
  const rawTab = searchParams.get('tab');
  const tabFromUrl = rawTab === 'contacts' ? 'leads' : rawTab;
  const activeTab: TabValue = isValidTab(tabFromUrl) ? tabFromUrl : 'live-feed';

  const setActiveTab = (tab: TabValue) => {
    setSearchParams({ tab }, { replace: true });
  };

  useEffect(() => {
    if (rawTab !== 'contacts') return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'leads');
    setSearchParams(next, { replace: true });
  }, [rawTab, searchParams, setSearchParams]);
  const [isEditing, setIsEditing] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const [contactsSearch, setContactsSearch] = useState('');
  const [addContactsOpen, setAddContactsOpen] = useState(false);
  const toolsToggleRef = useRef<(() => void) | null>(null);
  const [campaignName, setCampaignName] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [sequence, setSequence] = useState<SequenceStep[]>([{
    id: '1',
    type: 'email',
    delay: 0,
    delayUnit: 'days',
    subject: '',
    content: ''
  }]);
  const [selectedStepId, setSelectedStepId] = useState<string>('1');
  const [dailyLimit, setDailyLimit] = useState<number>(25);
  const [stopOnReply, setStopOnReply] = useState<boolean>(true);
  const [originalDailyLimit, setOriginalDailyLimit] = useState<number>(30);
  const [originalStopOnReply, setOriginalStopOnReply] = useState<boolean>(true);
  const [isSavingLaunchSettings, setIsSavingLaunchSettings] = useState(false);
  const [hideEditWarning, setHideEditWarning] = useState<boolean>(false);
  const [leads, setLeads] = useState<Array<{
    id: number | string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    title: string | null;
    company: string | null;
    companyWebsite: string | null;
    linkedinUrl: string | null;
    location: string | null;
    enrichmentStatus: string | null;
    prospectExternalId: string | null;
  }>>([]);

  const {
    campaign,
    isLoading,
    updateName,
    updateSequence,
    updateSettings,
    updateStatus,
    initializeRefs,
    getLeads,
    deleteLead,
    markAsViewed
  } = useCampaign(campaignId);

  // Track if this is the first time viewing this campaign (for grace period on first step)
  const [isFirstView, setIsFirstView] = useState(false);
  const hasMarkedAsViewed = useRef(false);
  const initialSequenceLengthRef = useRef<number | null>(null);
  const [initialFirstStepId, setInitialFirstStepId] = useState<string | null>(null);
  const [senderEmails, setSenderEmails] = useState<string[]>([]);

  const { data: _user } = useSWR<User>('/api/user', fetcher);
  const { data: emailAccountsData } = useSWR<any>('/api/email-accounts', fetcher);
  const { data: contactListsData } = useSWR<ContactList[]>('/api/contact-lists', fetcher);

  const emailAccounts = Array.isArray(emailAccountsData) ? emailAccountsData : [];
  const contactLists = Array.isArray(contactListsData) ? contactListsData : [];
  const linkedList = contactLists.find(l => l.campaignId === campaignId);
  const linkedListId = linkedList ? String(linkedList.id) : '';

  const handleSwitchList = async (newListId: string) => {
    if (!campaign || newListId === linkedListId) return;
    try {
      // Unlink old list
      if (linkedListId) {
        await fetch(`/api/contact-lists/${linkedListId}`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ campaignId: null }),
        });
      }
      // Link new list to this campaign
      await fetch(`/api/contact-lists/${newListId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId: String(campaign.id) }),
      });
      // Clear old campaign contacts and sync from new list
      await fetch(`/api/campaigns/${campaignId}/contacts/sync-from-list`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listId: parseInt(newListId) }),
      });
      toast.success('List switched');
      window.location.reload();
    } catch {
      toast.error('Failed to switch list');
    }
  };

  // Track the last loaded campaign ID to detect changes
  const lastLoadedCampaignIdRef = useRef<string | null>(null);
  const initialSequenceRef = useRef<SequenceStep[] | null>(null);

  // Validate sequence steps
  const getSequenceValidationErrors = (): ValidationError[] => {
    const errors: ValidationError[] = [];
    sequence.forEach((step, index) => {
      if (!step.subject || step.subject.trim() === '') {
        errors.push({
          stepId: step.id,
          stepIndex: index,
          field: 'subject',
          message: `Step ${index + 1} is missing a subject line`
        });
      }
      if (!step.content || step.content.trim() === '' || step.content === '<p></p>') {
        errors.push({
          stepId: step.id,
          stepIndex: index,
          field: 'content',
          message: `Step ${index + 1} is missing email content`
        });
      }
    });
    return errors;
  };

  const sequenceValidationErrors = getSequenceValidationErrors();
  const hasSequenceErrors = sequenceValidationErrors.length > 0;
  const sequenceCompleted = sequence.length > 0;
  const leadsCompleted = leads.length > 0;
  // The sender pool lives on the campaign, not on steps: at least one
  // connected mailbox, chosen in Settings. Each contact is assigned to one
  // member at its first send and keeps it for every step.
  const hasSenderPoolError = senderEmails.length === 0;
  const canLaunch = sequenceCompleted && leadsCompleted && !hasSequenceErrors && !hasSenderPoolError;

  // Initialize campaign data whenever campaign or campaignId changes
  useEffect(() => {
    if (!campaign || !campaignId) {
      return;
    }

    // Only initialize if this is a new campaign or the first load
    if (lastLoadedCampaignIdRef.current === campaignId) {
      return;
    }

    console.log('Initializing campaign:', campaignId);
    lastLoadedCampaignIdRef.current = campaignId;

    // Initialize state from campaign data
    setCampaignName(campaign.name || '');
    setEmailSubject(campaign.emailSubject || '');
    setEmailContent(campaign.emailContent || '');
    // Sender pool from the server; a campaign saved before pools existed
    // shows the senders its steps used, which is what it sends from.
    if (Array.isArray(campaign.senderEmails) && campaign.senderEmails.length > 0) {
      setSenderEmails(campaign.senderEmails);
    } else if (Array.isArray(campaign.sequence)) {
      setSenderEmails([...new Set((campaign.sequence as SequenceStep[]).map(st => st.senderEmail).filter((e): e is string => Boolean(e)))]);
    }

    if (campaign.sequence && Array.isArray(campaign.sequence) && campaign.sequence.length > 0) {
      setSequence(campaign.sequence as SequenceStep[]);
      initialSequenceRef.current = JSON.parse(JSON.stringify(campaign.sequence));
      initialSequenceLengthRef.current = campaign.sequence.length;

      // Select step from URL query param if present
      const stepFromUrl = searchParams.get('step');
      if (stepFromUrl) {
        const matchingStep = (campaign.sequence as SequenceStep[]).find(s => s.id === stepFromUrl);
        if (matchingStep) {
          setSelectedStepId(matchingStep.id);
        }
      }
    } else {
      const defaultSequence: SequenceStep[] = [{
        id: '1',
        type: 'email',
        delay: 0,
        delayUnit: 'days',
        subject: '',
        content: ''
      }];
      setSequence(defaultSequence);
      initialSequenceRef.current = JSON.parse(JSON.stringify(defaultSequence));
      initialSequenceLengthRef.current = 1;
    }

    // Prefer the real columns (campaign.dailyLimit / .stopOnReply). Fall back
    // to the legacy targetAudience JSON blob for campaigns saved before the
    // settings moved to dedicated columns. hideEditWarning has no column —
    // still lives in the JSON blob.
    if (typeof campaign.dailyLimit === 'number') {
      setDailyLimit(campaign.dailyLimit);
      setOriginalDailyLimit(campaign.dailyLimit);
    }
    if (typeof campaign.stopOnReply === 'boolean') {
      setStopOnReply(campaign.stopOnReply);
      setOriginalStopOnReply(campaign.stopOnReply);
    }
    if (campaign.targetAudience) {
      try {
        const legacy = JSON.parse(campaign.targetAudience);
        if (typeof campaign.dailyLimit !== 'number' && legacy.dailyLimit !== undefined) {
          setDailyLimit(legacy.dailyLimit);
          setOriginalDailyLimit(legacy.dailyLimit);
        }
        if (typeof campaign.stopOnReply !== 'boolean' && legacy.stopOnReply !== undefined) {
          setStopOnReply(legacy.stopOnReply);
          setOriginalStopOnReply(legacy.stopOnReply);
        }
        if (legacy.hideEditWarning !== undefined) {
          setHideEditWarning(legacy.hideEditWarning);
        }
      } catch (e) {
        // not JSON — pre-Phase-2 free-text targetAudience, ignore
      }
    }

    initializeRefs(campaign);

    // Check if this is the first time viewing this campaign
    // Use falsy check to handle both null and undefined
    if (!hasMarkedAsViewed.current && !campaign.lastViewedAt) {
      setIsFirstView(true);
      // Capture the first step ID for grace period
      const firstStepId = campaign.sequence?.[0]?.id;
      if (firstStepId) {
        setInitialFirstStepId(firstStepId);
      }
      markAsViewed();
      hasMarkedAsViewed.current = true;
    }
  }, [campaign, campaignId, initializeRefs, markAsViewed]);

  // Load the campaign's contacts. Held as a callback so an import can pull
  // the table fresh without waiting for a navigation.
  const refreshLeads = useCallback(async () => {
    const fetchedLeads = await getLeads();
    setLeads(fetchedLeads.map(lead => ({
      id: lead.id,
      contactId: lead.contactId,
      email: lead.email,
      firstName: lead.firstName,
      lastName: lead.lastName,
      title: lead.title,
      company: lead.company,
      companyWebsite: lead.companyWebsite,
      linkedinUrl: lead.linkedinUrl,
      location: lead.location,
      enrichmentStatus: lead.enrichmentStatus,
      enrichmentConfidence: lead.enrichmentConfidence ?? null,
      emailStatus: lead.emailStatus ?? null,
      emailCheckedAt: lead.emailCheckedAt ?? null,
      emailCheckStatus: lead.emailCheckStatus ?? null,
      prospectExternalId: (lead as any).prospectExternalId || null,
    })));
  }, [getLeads]);

  // Fetch leads when campaign is loaded
  useEffect(() => {
    if (campaign && campaignId) {
      refreshLeads().catch(() => {
        // Error already handled in getLeads
      });
    }
  }, [campaign, campaignId, refreshLeads]);

  // Update hideEditWarning when targetAudience changes
  useEffect(() => {
    if (campaign?.targetAudience) {
      try {
        const settings = JSON.parse(campaign.targetAudience);
        if (settings.hideEditWarning !== undefined) {
          setHideEditWarning(settings.hideEditWarning);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, [campaign?.targetAudience]);

  // Ensure there's always a selected step
  useEffect(() => {
    if (sequence.length > 0 && !sequence.find(step => step.id === selectedStepId)) {
      setSelectedStepId(sequence[sequence.length - 1].id);
    }
  }, [sequence, selectedStepId]);

  // Clear first view grace period when user adds/removes steps
  useEffect(() => {
    if (isFirstView && initialSequenceLengthRef.current !== null && sequence.length !== initialSequenceLengthRef.current) {
      setIsFirstView(false);
    }
  }, [sequence.length, isFirstView]);

  // Auto-save campaign name
  useEffect(() => {
    if (!campaign || !campaignId) return;
    if (lastLoadedCampaignIdRef.current !== campaignId) return;
    if (!campaignName || !campaignName.trim()) return;
    if (campaignName === campaign.name) return; // Don't save if unchanged

    const timeoutId = setTimeout(() => {
      updateName(campaignName);
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [campaignName, campaign, campaignId, updateName]);

  // Auto-save sequence
  useEffect(() => {
    if (!campaign || !campaignId) return;
    if (lastLoadedCampaignIdRef.current !== campaignId) return;

    if (initialSequenceRef.current !== null) {
      const initialSeqString = JSON.stringify(initialSequenceRef.current);
      const currentSeqString = JSON.stringify(sequence);
      if (initialSeqString === currentSeqString) {
        return;
      }
    }

    const timeoutId = setTimeout(() => {
      updateSequence(sequence);
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [sequence, campaign, campaignId, updateSequence]);

  // Auto-save settings
  useEffect(() => {
    if (!campaign || !campaignId) return;
    if (lastLoadedCampaignIdRef.current !== campaignId) return;
    if (emailSubject === campaign.emailSubject && emailContent === campaign.emailContent) return;

    const timeoutId = setTimeout(() => {
      updateSettings({
        emailSubject,
        emailContent,
      });
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [emailSubject, emailContent, campaign, campaignId, updateSettings]);

  // Reset on unmount
  useEffect(() => {
    return () => {
      lastLoadedCampaignIdRef.current = null;
    };
  }, []);

  // Handlers
  const hasUnsavedLaunchSettings = dailyLimit !== originalDailyLimit || stopOnReply !== originalStopOnReply;

  const handleSenderEmailsChange = async (next: string[]) => {
    const previous = senderEmails;
    setSenderEmails(next);
    try {
      await updateSettings({ senderEmails: next });
    } catch {
      setSenderEmails(previous);
      toast.error('Could not save the sending mailboxes');
    }
  };

  const handleLaunch = async () => {
    if (!campaign) {
      toast.error('Campaign not found');
      return;
    }

    if (!canLaunch) {
      if (hasSenderPoolError) {
        toast.error('Choose at least one sending mailbox');
        setActiveTab('sequence');
        return;
      }
      if (hasSequenceErrors) {
        const subjectErrors = sequenceValidationErrors.filter(e => e.field === 'subject');
        const contentErrors = sequenceValidationErrors.filter(e => e.field === 'content');
        const senderErrors = sequenceValidationErrors.filter(e => e.field === 'senderEmail');
        
        if (subjectErrors.length > 0 && contentErrors.length > 0 && senderErrors.length > 0) {
          toast.error(`Please fill in all email subjects, content, and sender emails before launching`);
        } else if (subjectErrors.length > 0 && contentErrors.length > 0) {
          toast.error(`Please fill in all email subjects and content before launching`);
        } else if (subjectErrors.length > 0 && senderErrors.length > 0) {
          toast.error(`Please fill in subject lines and sender emails for ${[...new Set([...subjectErrors, ...senderErrors].map(e => `Step ${e.stepIndex + 1}`))].join(', ')}`);
        } else if (contentErrors.length > 0 && senderErrors.length > 0) {
          toast.error(`Please fill in email content and sender emails for ${[...new Set([...contentErrors, ...senderErrors].map(e => `Step ${e.stepIndex + 1}`))].join(', ')}`);
        } else if (subjectErrors.length > 0) {
          toast.error(`Please fill in the subject line for ${subjectErrors.map(e => `Step ${e.stepIndex + 1}`).join(', ')}`);
        } else if (contentErrors.length > 0) {
          toast.error(`Please fill in the email content for ${contentErrors.map(e => `Step ${e.stepIndex + 1}`).join(', ')}`);
        } else if (senderErrors.length > 0) {
          toast.error(`Please select a sender email address for ${senderErrors.map(e => `Step ${e.stepIndex + 1}`).join(', ')}`);
        }
        setActiveTab('sequence');
        if (sequenceValidationErrors.length > 0) {
          setSelectedStepId(sequenceValidationErrors[0].stepId);
        }
        return;
      }
      if (!leadsCompleted) {
        toast.error('Please add at least one lead to the campaign');
        setActiveTab('leads');
        return;
      }
      return;
    }
    
    setIsEditing(true);
    try {
      if (campaignName !== campaign.name) {
        await updateName(campaignName);
      }
      
      await updateSequence(sequence);
      
      if (hasUnsavedLaunchSettings) {
        // dailyLimit / stopOnReply go to their real columns on the campaigns
        // table, NOT into the targetAudience JSON blob — the mailer reads
        // campaign.daily_limit directly and ignores targetAudience.
        await updateSettings({ dailyLimit, stopOnReply });
        setOriginalDailyLimit(dailyLimit);
        setOriginalStopOnReply(stopOnReply);
      }
      
      await updateStatus('active');
      
      try {
        const syncResponse = await fetch(`/api/campaigns/${campaignId}/sync`, {
          method: 'POST',
        });
        
        if (!syncResponse.ok) {
          console.error('Failed to sync campaign to mailer');
        }
      } catch (syncError) {
        console.error('Error syncing campaign:', syncError);
      }
    } catch (error) {
      console.error('Failed to launch campaign:', error);
      toast.error('Failed to launch campaign');
    } finally {
      setIsEditing(false);
    }
  };

  const handlePause = async () => {
    if (!campaign) {
      toast.error('Campaign not found');
      return;
    }
    
    setIsEditing(true);
    try {
      await updateStatus('paused');
    } catch (error) {
      console.error('Failed to pause campaign:', error);
      toast.error('Failed to pause campaign');
    } finally {
      setIsEditing(false);
    }
  };

  const handleSaveLaunchSettings = async () => {
    if (!campaign) {
      toast.error('Campaign not found');
      return;
    }

    setIsSavingLaunchSettings(true);
    try {
      // dailyLimit / stopOnReply persist to the real campaign columns. Only
      // hideEditWarning (a UI-only preference) still goes into the
      // targetAudience JSON blob, since there's no column for it.
      await updateSettings({ dailyLimit, stopOnReply });
      await persistHideEditWarning(hideEditWarning);

      setOriginalDailyLimit(dailyLimit);
      setOriginalStopOnReply(stopOnReply);
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save launch settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSavingLaunchSettings(false);
    }
  };

  // Persist the UI-only hideEditWarning preference into the targetAudience
  // JSON blob without clobbering anything else stored there. Kept in
  // targetAudience because there's no dedicated column — and there's no
  // reason to add one for a single boolean we never read server-side.
  const persistHideEditWarning = async (hide: boolean) => {
    let existing: Record<string, unknown> = {};
    if (campaign?.targetAudience) {
      try { existing = JSON.parse(campaign.targetAudience); } catch { /* not JSON, ignore */ }
    }
    await updateSettings({ targetAudience: JSON.stringify({ ...existing, hideEditWarning: hide }) });
  };

  const handleHideEditWarningChange = async (hide: boolean) => {
    setHideEditWarning(hide);
    try {
      await persistHideEditWarning(hide);
      toast.success('Preference saved');
    } catch (error) {
      console.error('Failed to save hideEditWarning setting:', error);
      toast.error('Failed to save preference');
      setHideEditWarning(!hide);
    }
  };

  // All tabs always visible
  const tabOptions: { value: TabValue; label: string; icon?: React.ReactNode }[] = [
    { value: 'live-feed', label: 'Live Feed' },
    { value: 'leads', label: 'Leads' },
    { value: 'sequence', label: 'Sequence' },
    { value: 'sends', label: 'Sends' },
    { value: 'settings', label: 'Settings & Launch' },
  ];

  const handleToggleStatus = async () => {
    if (campaign?.status === 'active') {
      await handlePause();
    } else if (canLaunch) {
      await handleLaunch();
    }
  };

  // Shown by every tab while the campaign is still loading, or when the id
  // doesn't resolve to a campaign.
  const campaignFallback = (
    <div className="flex items-center justify-center h-64">
      {isLoading ? (
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      ) : (
        <p className="text-muted-foreground">Campaign not found</p>
      )}
    </div>
  );

  return (
    <PageShell>
      {/* Row 1: Back button, name, tabs, status toggle. Every tab, Settings
          included, sits in the panel below, whose edge bounds the row's
          shadows, so the row needs no lift. */}
      <PageTopRow>
        <BackButton href="/dashboard/campaigns" tooltip="Back to Campaigns" />
        <div className="w-fit ml-2">
          <Toggle<TabValue>
            options={tabOptions}
            value={activeTab}
            onChange={setActiveTab}
            className="[&_button]:text-[0.9rem] [&_button]:!px-6 shadow-2xl"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
        {isRenaming ? (
          <Input
            ref={renameInputRef}
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            onBlur={() => { setIsRenaming(false); updateName(campaignName); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setIsRenaming(false); updateName(campaignName); } if (e.key === 'Escape') { setIsRenaming(false); setCampaignName(campaign?.name || ''); } }}
            className="text-sm font-medium focus:ring-0 pl-5 pr-5 w-72 h-12 rounded-full !mb-0 mr-4"
            placeholder="Campaign name"
            autoFocus
          />
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 text-sm font-medium bg-transparent hover:bg-accent rounded-full px-5 h-12 transition-colors max-w-80 cursor-pointer">
                <span className="truncate">{campaignName || 'Untitled'}</span>
                <ChevronDown className="h-3.5 w-3.5 text-content-secondary shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => { setIsRenaming(true); setTimeout(() => renameInputRef.current?.select(), 0); }}>
                <Pencil className="h-4 w-4" />
                Rename
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {campaign?.status === 'active' ? (
          <Button
            variant="secondary"
            onClick={handleToggleStatus}
            disabled={isEditing}
          >
            <PauseIcon className="h-4 w-4 text-danger-text" />
            Pause Campaign
          </Button>
        ) : (
          <Tooltip open={canLaunch ? false : undefined}>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="secondary"
                  onClick={handleToggleStatus}
                  disabled={isEditing || !canLaunch}
                >
                  <PlayIcon className="!h-5 !w-5 text-success-solid" />
                  Resume Campaign
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="font-medium mb-1">Campaign isn't ready yet</p>
              <ul className="text-xs list-disc pl-3 space-y-0.5">
                {!sequenceCompleted && <li>Add at least one sequence step</li>}
                {hasSequenceErrors && <li>Fix sequence errors (missing subject or content)</li>}
                {hasSenderPoolError && <li>Choose at least one sending mailbox</li>}
                {!leadsCompleted && <li>Add leads to this campaign</li>}
              </ul>
            </TooltipContent>
          </Tooltip>
        )}
        </div>
      </PageTopRow>

      {/* The active tab's page, in one tinted panel; the tab bar stays
          outside it. */}
      <ContentCard tinted={activeTab === 'sequence' || activeTab === 'settings'} flush toolbar={activeTab === 'leads' || activeTab === 'sends'}>
      {/* Contextual controls for the leads tab */}
      {activeTab === 'leads' && (
        <div className="mb-header-gap flex items-center justify-between">
          <div>
            <Select value={linkedListId} onValueChange={handleSwitchList}>
              <SelectTrigger className="w-72 h-12 text-sm" inlineLabel="List">
                <SelectValue placeholder="No list linked" />
              </SelectTrigger>
              <SelectContent>
                {contactLists.map((list) => (
                  <SelectItem key={list.id} value={String(list.id)}>
                    {list.name} ({list.memberCount})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Input
              placeholder="Search leads"
              value={contactsSearch}
              onChange={(e) => setContactsSearch(e.target.value)}
              className="max-w-sm w-52"
              prefixIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
            />
            <Button variant="primary" onClick={() => setAddContactsOpen(true)}>
              <Plus className="h-4 w-4" />
              Add Leads
            </Button>
            <ToolsButton onClick={() => toolsToggleRef.current?.()} hasEnabled={false} />
          </div>
        </div>
      )}

      {/* Tab Content */}
      {!campaign ? campaignFallback : (
        <>
          {activeTab === 'live-feed' && (
            <LiveFeedTab agentId={campaign.agentId || undefined} />
          )}

          {activeTab === 'leads' && (
            <div className="flex flex-col flex-1 min-h-0 -mx-page-inset">
              <ContactsTable
                inCard
                contacts={leads as any}
                isLoading={!campaign}
                deleteContact={async (id) => {
                  try { await deleteLead(String(id)); setLeads(prev => prev.filter(l => l.id !== id)); return true; } catch { return false; }
                }}
                bulkDelete={async (ids) => {
                  try { await Promise.all(ids.map(id => deleteLead(String(id)))); setLeads(prev => prev.filter(l => !ids.includes(l.id as number))); return true; } catch { return false; }
                }}
                updateContact={async (id, field, value) => {
                  try {
                    await fetch(`/api/campaigns/${campaignId}/leads/${id}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }) });
                    setLeads(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
                    return true;
                  } catch { return false; }
                }}
                searchQuery={contactsSearch}
                onClearSearch={() => setContactsSearch('')}
                emptyMessage="No leads in this campaign yet. Add leads from a list."
                noun={{ singular: 'lead', plural: 'leads' }}
                toolsButtonRef={toolsToggleRef}
              />
            </div>
          )}

          {activeTab === 'sequence' && (
            <div className="flex-1 min-h-0">
              <SequenceTab
                sequence={sequence}
                setSequence={setSequence}
                selectedStepId={selectedStepId}
                setSelectedStepId={setSelectedStepId}
                emailAccounts={emailAccounts}
                isReadOnly={campaign?.status === 'active'}
                hideEditWarning={hideEditWarning}
                onHideEditWarningChange={handleHideEditWarningChange}
                validationErrors={sequenceValidationErrors}
                isNewCampaign={isFirstView}
                initialFirstStepId={initialFirstStepId}
                leads={leads}
                senderEmails={senderEmails}
                onChangeSenderEmails={handleSenderEmailsChange}
              />
            </div>
          )}

          {activeTab === 'sends' && (
            <SendsTab inCard agentId={campaign.agentId} campaignId={campaignId} senderEmails={senderEmails} senderSummary={campaign.senderSummary} />
          )}

          {activeTab === 'settings' && (
            <SettingsTab
              campaign={campaign}
              dailyLimit={dailyLimit}
              setDailyLimit={setDailyLimit}
              stopOnReply={stopOnReply}
              setStopOnReply={setStopOnReply}
              hasUnsavedSettings={hasUnsavedLaunchSettings}
              isSaving={isSavingLaunchSettings}
              handleSaveSettings={handleSaveLaunchSettings}
              handleLaunch={handleLaunch}
              handlePause={handlePause}
              isEditing={isEditing}
              canLaunch={canLaunch}
              validationErrors={sequenceValidationErrors}
              leadsCompleted={leadsCompleted}
              senderEmails={senderEmails}
              onChangeSenderEmails={handleSenderEmailsChange}
              onGoToSequence={() => setActiveTab('sequence')}
              onGoToContacts={() => setActiveTab('leads')}
            />
          )}

        </>
      )}
      </ContentCard>
      <AddContactsPanel
        open={addContactsOpen}
        onClose={() => setAddContactsOpen(false)}
        campaignId={campaignId}
        campaignIcp={(campaign as any)?.filterJson ?? null}
        onBatchCreated={(shortId) => setContactsSearch(`batch:${shortId}`)}
        onImported={() => { void refreshLeads(); }}
      />
    </PageShell>
  );
}
